import React, { useCallback, useEffect, useMemo } from 'react';
import { List } from 'immutable';
import { useSelector } from 'react-redux';
import { approversSelector } from '@/domain/approvals';

import isEmpty from 'lodash/isEmpty';

import { SYMBOL_FOR_EMPTY_CELL } from '@/components/AgGrid/helpers';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CountChip from '@/components/AgGrid/components/TagsCellRenderer/CountChip';
import Approver from '@/pages/components/Approver';
import ApproverLevels from '@/pages/components/ApproverLevels';
import Avatar from '@mui/material/Avatar';
import useObserverTags from '@/components/AgGrid/components/TagsCellRenderer/useObserverTags';

const createApprovalFactory = (approvers, options = {}) => {
  const { idKey = 'userid', pictureKey = 'picture' } = options;
  return ({ username = '', status = '', ...rest }) => {
    const approver = approvers.find((u) => u.username === username);
    return {
      id: rest[idKey],
      picture: approver?.[pictureKey],
      status,
      fullName: approver?.fullName || username,
    };
  };
};

const ApprovalsCellRenderer = ({ valueFormatted, value, api, column, selectorOfApprovers, approvalFactoryOptions }) => {
  const selector =
    selectorOfApprovers && typeof selectorOfApprovers === 'function' ? selectorOfApprovers : approversSelector;

  const approvers = useSelector(selector);

  const [columnWidth, setColumnWidth] = React.useState(column.getActualWidth());

  const ApprovalFactory = useMemo(
    () => createApprovalFactory(approvers, approvalFactoryOptions),
    [approvers, approvalFactoryOptions],
  );

  const handleColumnResize = useCallback(() => {
    setColumnWidth(column.getActualWidth());
  }, [column]);

  const isAdvancedGroups = useMemo(() => !!value?.levels, [value]);

  const list = useMemo(() => {
    if (isAdvancedGroups) {
      return value.levels.map(({ nodes: levelNodes, flow_type: flowType, ...rest }) => ({
        ...rest,
        flowType,
        approvers: levelNodes.map(ApprovalFactory),
      }));
    }

    return value ? value.map(ApprovalFactory) : [];
  }, [value, isAdvancedGroups, ApprovalFactory]);

  const uniqueKeysList = useMemo(() => List(list.map(({ id }) => id)), [list]);

  const { parentElRef, setElementRef, elementsVisibilityState, hiddenCount, lastVisibleKey, isAllHidden, lastKey } =
    useObserverTags({ uniqueKeysList });

  useEffect(() => {
    if (isAdvancedGroups) {
      handleColumnResize();
      api.addEventListener('columnResized', handleColumnResize);
    }

    return () => {
      if (isAdvancedGroups) {
        api.removeEventListener('columnResized', handleColumnResize);
      }
    };
  }, [isAdvancedGroups, api, handleColumnResize]);

  if (list.length === 0) {
    return <Box sx={{ px: 2 }}>{isEmpty(valueFormatted) ? SYMBOL_FOR_EMPTY_CELL : valueFormatted}</Box>;
  }

  if (isAdvancedGroups) {
    return <ApproverLevels value={list} maxWidth={columnWidth} withStatus hideExtra />;
  }

  return (
    <Stack direction="row" flexWrap="nowrap" spacing={0.5} maxWidth="100%" px={0.5} ref={parentElRef}>
      {isAllHidden && <CountChip size="small" label={list.length} />}
      {list.map(({ fullName, picture, id, status }) => {
        const isVisible = elementsVisibilityState.get(id, 1) === 1;
        const isLastVisible = lastVisibleKey === id;
        const shouldShowCountChip = isLastVisible && hiddenCount > 0 && !isAllHidden;

        return (
          <Stack key={id} direction="row" flexWrap="nowrap" spacing={0.5} ref={setElementRef(id)}>
            <Approver
              size="small"
              spacing={0.5}
              sx={{
                opacity: isVisible ? 1 : 0,
                visibility: isVisible ? 'visible' : 'hidden',
              }}
              avatar={<Avatar src={picture} alt={fullName} />}
              status={status}
              label={fullName}
              hasArrow={lastKey !== id}
            />
            {shouldShowCountChip && <CountChip size="small" label={hiddenCount} />}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ApprovalsCellRenderer;
